import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Keeps a merchant-configured free gift line in sync with cart eligibility
 * and, when a trigger product is configured, scales the gift's own quantity
 * with the trigger product's quantity (e.g. 2 free spoons per 1 Diabet Cure,
 * 4 for 2, etc).
 *
 * Reads its configuration from data attributes on <cart-items-component>
 * (written by snippets/cart-items-component.liquid, itself fed by
 * snippets/cart-drawer.liquid and sections/main-cart.liquid from the
 * cart-drawer-section's theme-editor settings), so changing the gift
 * product, trigger product, minimum subtotal, or quantity-per-trigger-unit
 * in the theme editor takes effect with no code changes. <cart-items-component>
 * is used for both the cart drawer and the cart page, so this works
 * identically on both.
 *
 * Two eligibility modes, chosen per the configured settings:
 *  - Trigger product mode (data-gift-trigger-variant-id set): the gift's
 *    target quantity is (trigger product's quantity in cart) *
 *    (data-gift-quantity-per-trigger-unit, default 2). Zero when the trigger
 *    product isn't in the cart.
 *  - Subtotal mode (no trigger product configured): the gift's target
 *    quantity is 1 once the cart subtotal (excluding the gift line) reaches
 *    the configured minimum, otherwise 0.
 *
 * Rides the theme's existing cart-update event bus (the same
 * StandardEvents.cartLinesUpdate / CartLinesUpdateEvent contract every other
 * cart component and third-party app already uses) instead of polling or
 * owning its own render path — cart-items-component and cart-icon pick up
 * the resulting section HTML themselves.
 *
 * Note: the `cart` object attached to the event's resolved promise does not
 * reliably include `.items` for every action that can trigger the event
 * (its shape varies by which component dispatched it — add-to-cart form,
 * quantity stepper, discount code, etc.). Rather than depend on that shape,
 * every reconcile always re-fetches the authoritative cart from /cart.js.
 */
const GIFT_SOURCE = 'cart-gift-manager';

/** Re-entrancy guard: one add/remove/update mutation in flight at a time. */
let mutationInFlight = false;

/**
 * @returns {{variantId: number, triggerVariantId: number | null, quantityPerTriggerUnit: number, minimumSubtotalCents: number} | null}
 */
function readGiftConfig() {
  const host = document.querySelector('cart-items-component[data-gift-variant-id]');
  if (!host) return null;

  const variantId = Number(host.getAttribute('data-gift-variant-id'));
  const rawTriggerVariantId = host.getAttribute('data-gift-trigger-variant-id');
  const triggerVariantId =
    rawTriggerVariantId && Number.isFinite(Number(rawTriggerVariantId)) && Number(rawTriggerVariantId) > 0
      ? Number(rawTriggerVariantId)
      : null;
  const rawQuantityPerTriggerUnit = host.getAttribute('data-gift-quantity-per-trigger-unit');
  const parsedQuantityPerTriggerUnit = Number(rawQuantityPerTriggerUnit);
  const quantityPerTriggerUnit =
    Number.isFinite(parsedQuantityPerTriggerUnit) && parsedQuantityPerTriggerUnit > 0
      ? parsedQuantityPerTriggerUnit
      : 2;
  const minimumSubtotalCents = Number(host.getAttribute('data-gift-minimum-subtotal-cents'));

  if (!Number.isFinite(variantId) || variantId <= 0) return null;
  if (!triggerVariantId && (!Number.isFinite(minimumSubtotalCents) || minimumSubtotalCents < 0)) return null;

  return { variantId, triggerVariantId, quantityPerTriggerUnit, minimumSubtotalCents };
}

/**
 * @returns {string} Comma-separated list of every rendered cart-items-component's section id.
 */
function collectSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
    const id = element.getAttribute('data-section-id');
    if (id) ids.add(id);
  });
  return Array.from(ids).join(',');
}

/**
 * @param {{variantId: number}} config
 * @param {{items: Array<{variant_id: number, key: string, quantity: number}>}} cart
 */
function findGiftLine(config, cart) {
  return cart.items?.find((item) => item.variant_id === config.variantId) ?? null;
}

/**
 * @param {{triggerVariantId: number}} config
 * @param {{items: Array<{variant_id: number, quantity: number}>}} cart
 * @returns {number} Total quantity of the configured trigger product currently in the cart (0 if absent).
 */
function triggerQuantityInCart(config, cart) {
  return (cart.items ?? [])
    .filter((item) => item.variant_id === config.triggerVariantId)
    .reduce((sum, item) => sum + (item.quantity || 0), 0);
}

/**
 * @param {{variantId: number}} config
 * @param {{items: Array<{variant_id: number, final_line_price: number}>}} cart
 * @returns {number} Cart subtotal in cents, excluding the gift line itself.
 */
function subtotalExcludingGift(config, cart) {
  return (cart.items ?? [])
    .filter((item) => item.variant_id !== config.variantId)
    .reduce((sum, item) => sum + item.final_line_price, 0);
}

/**
 * Sets the gift line to an exact quantity via Shopify's Ajax Cart API
 * (/cart/update.js "updates" sets the line's quantity to the given value,
 * it does not add on top of the existing quantity) and broadcasts the
 * result on the same event every other cart component listens to, so the
 * drawer/page/badge all update themselves.
 * @param {number} targetQuantity - 0 removes the line entirely.
 * @param {number} variantId
 */
async function setGiftLineQuantity(targetQuantity, variantId) {
  const sections = collectSectionIds();
  const body = JSON.stringify({
    updates: { [variantId]: targetQuantity },
    sections,
    sections_url: window.location.pathname,
  });

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: targetQuantity > 0 ? 'add' : 'remove',
      context: 'cart',
      lines: [{ id: String(variantId), quantity: targetQuantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    const parsed = await response.json();

    if (parsed.errors) {
      throw new Error(typeof parsed.errors === 'string' ? parsed.errors : JSON.stringify(parsed.errors));
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsed),
      detail: {
        sections: parsed.sections,
        items: parsed.items,
        itemCount: parsed.item_count,
        source: GIFT_SOURCE,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error instanceof Error ? error : new Error(String(error)));
    console.warn('[cart-gift-manager] Failed to sync free gift:', error);
  }
}

/**
 * Compares live cart contents against the eligibility rule and the gift's
 * current quantity, and adds, removes, or resizes the gift line to match.
 * In trigger-product mode the target quantity scales with the trigger
 * product's quantity (quantityPerTriggerUnit per unit); in subtotal mode
 * the target quantity is always 1 once eligible. No-ops while a prior
 * mutation is still in flight, or once the cart already matches the target
 * quantity.
 * @param {{items: Array<{variant_id: number, key: string, quantity: number, final_line_price: number}>}} cart
 */
async function reconcile(cart) {
  if (mutationInFlight) return;
  if (!cart || !Array.isArray(cart.items)) return;

  const config = readGiftConfig();
  if (!config) return;

  let targetQuantity;
  if (config.triggerVariantId) {
    const triggerQuantity = triggerQuantityInCart(config, cart);
    targetQuantity = triggerQuantity > 0 ? triggerQuantity * config.quantityPerTriggerUnit : 0;
  } else {
    const eligible = subtotalExcludingGift(config, cart) >= config.minimumSubtotalCents;
    targetQuantity = eligible ? 1 : 0;
  }

  const giftLine = findGiftLine(config, cart);
  const currentQuantity = giftLine ? giftLine.quantity : 0;

  if (currentQuantity === targetQuantity) return;

  mutationInFlight = true;
  try {
    await setGiftLineQuantity(targetQuantity, config.variantId);
  } finally {
    mutationInFlight = false;
  }
}

/**
 * Fetches the authoritative cart from the Ajax API and reconciles against
 * it. Used instead of trusting the shape of whatever cart object (if any)
 * rides along on a cart-lines-update event, since that shape varies by
 * dispatcher and isn't guaranteed to include `.items`.
 */
async function reconcileFromServer() {
  const config = readGiftConfig();
  if (!config) return;
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return;
    await reconcile(await response.json());
  } catch (error) {
    console.warn('[cart-gift-manager] Reconcile failed:', error);
  }
}

/** @param {CartLinesUpdateEvent} event */
function onCartLinesUpdate(event) {
  event.promise
    ?.then((result) => {
      // Skip reconciling against our own mutation's result — it already
      // reflects the target state, and re-checking now would fight with
      // the in-flight guard used by the mutation that produced it.
      if (result?.detail?.source === GIFT_SOURCE) return;
      reconcileFromServer();
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-gift-manager] Event promise rejected:', error);
    });
}

document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);
reconcileFromServer();
